import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { IconX as X } from '@tabler/icons-react';
import { fetchAuditLogs, type AuditLogEntry } from '@/api/adminApi';
import { input } from '@/components/ui/design';
import EventEditor from './EventEditor';
import OrganizationEditor from './OrganizationEditor';
import { EmptyRow, Pager, SearchBox, pill, rowBorder, smallBtn, tableWrap, td, th, useDebounced } from './shared';

const LIMIT = 25;
const RESOURCES = ['events', 'tickets', 'venues', 'seat-assignments', 'orders', 'payments', 'payouts', 'contacts', 'organizer', 'organizers', 'members', 'promo-codes', 'messages', 'event-reports', 'upload', 'organizations', 'users', 'cache'];
const METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];
type Drawer = { kind: 'event' | 'organization'; id: string };
const drawerFor = (entry: AuditLogEntry): Drawer | null => {
    if (!entry.resourceId) return null;
    if ((entry.resource === 'events' && !entry.path.includes('/comments')) || entry.resource === 'tickets') return { kind: 'event', id: entry.resourceId };
    if (entry.resource === 'organizer' || entry.resource === 'organizations') return { kind: 'organization', id: entry.resourceId };
    return null;
};
const fmt = (value: unknown) => (typeof value === 'string' ? value : JSON.stringify(value)) ?? 'empty';

const ChangesModal = ({ entry, onClose }: { entry: AuditLogEntry; onClose: () => void }) => (
    <div className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/50 p-4" onMouseDown={e => e.target === e.currentTarget && onClose()}>
        <div role="dialog" aria-modal="true" aria-label="Changes" className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-[28px] bg-white p-6 shadow-2xl dark:bg-nightSurface">
            <div className="mb-4 flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h2 className="text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white">Changes</h2>
                    <p className="break-all text-sm text-grey40 dark:text-nightMuted">{entry.method} {entry.path.replace('/eventopia/', '')}</p>
                    <p className="text-sm text-grey40 dark:text-nightMuted">{entry.actorEmail} · {new Date(entry.createdAt).toLocaleString()}</p>
                </div>
                <button type="button" onClick={onClose} aria-label="Close" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full ring-1 ring-grey90 hover:bg-grey99 dark:text-white dark:ring-white/15 dark:hover:bg-white/10"><X className="h-4 w-4" /></button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto text-sm text-grey10 dark:text-white">
                {entry.uploaded?.map(field => <div key={field} className="mb-2">Uploaded new file to <b>{field}</b></div>)}
                {entry.changes && Object.entries(entry.changes).map(([field, change]) => (
                    <div key={field} className="mb-3">
                        <b>{field}</b>
                        <div className="break-all text-red-500">- {fmt(change.before)}</div>
                        <div className="break-all text-green-600">+ {fmt(change.after)}</div>
                    </div>
                ))}
                {!entry.changes && entry.body ? <pre className="whitespace-pre-wrap text-xs">{JSON.stringify(entry.body, null, 2)}</pre> : null}
                {!entry.changes && !entry.body && !entry.uploaded?.length && <p className="text-grey40 dark:text-nightMuted">No field-level details recorded for this action.</p>}
            </div>
        </div>
    </div>
);

export default function AuditLogTab() {
    const [drawer, setDrawer] = useState<Drawer | null>(null);
    const [selected, setSelected] = useState<AuditLogEntry | null>(null);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState({ resource: '', resourceId: '', method: '', from: '', to: '' });
    const setFilter = (key: keyof typeof filters, value: string) => { setFilters(prev => ({ ...prev, [key]: value })); setPage(1); };
    const debouncedId = useDebounced(filters.resourceId.trim());
    const debounced = useDebounced(search.trim());
    const { data, isLoading, isError } = useQuery({
        queryKey: ['admin-audit-logs', page, debounced, debouncedId, filters.resource, filters.method, filters.from, filters.to],
        queryFn: () => fetchAuditLogs({ page, limit: LIMIT, search: debounced || undefined, resourceId: debouncedId || undefined, resource: filters.resource || undefined, method: filters.method || undefined, from: filters.from || undefined, to: filters.to || undefined }),
        placeholderData: previous => previous,
    });

    return (
        <div>
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
                <SearchBox value={search} onChange={value => { setSearch(value); setPage(1); }} placeholder="Search by user email" />
                <select value={filters.resource} onChange={e => setFilter('resource', e.target.value)} className={`${input} md:max-w-[180px]`} aria-label="Section">
                    <option value="">All sections</option>
                    {RESOURCES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <select value={filters.method} onChange={e => setFilter('method', e.target.value)} className={`${input} md:max-w-[140px]`} aria-label="Method">
                    <option value="">All methods</option>
                    {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <input value={filters.resourceId} onChange={e => setFilter('resourceId', e.target.value)} placeholder="Record ID" className={`${input} md:max-w-[220px]`} aria-label="Record ID" />
                <input type="date" value={filters.from} onChange={e => setFilter('from', e.target.value)} className={`${input} md:max-w-[160px]`} aria-label="From date" />
                <input type="date" value={filters.to} onChange={e => setFilter('to', e.target.value)} className={`${input} md:max-w-[160px]`} aria-label="To date" />
            </div>
            {isError && <p className="mb-3 text-sm text-red-500">Failed to load audit log.</p>}
            <div className={tableWrap}>
                <table className="w-full min-w-[820px]">
                    <thead>
                        <tr><th className={th}>When</th><th className={th}>User</th><th className={th}>Action</th><th className={th}>Target</th><th className={th}>Changes</th></tr>
                    </thead>
                    <tbody>
                        {(data?.data ?? []).map(entry => (
                            <tr key={entry._id} className={rowBorder}>
                                <td className={`${td} whitespace-nowrap`}>{new Date(entry.createdAt).toLocaleString()}</td>
                                <td className={td}>
                                    {entry.actorName || entry.actorEmail}
                                    {entry.actorName && <div className="text-grey40 dark:text-nightMuted">{entry.actorEmail}</div>}
                                    {entry.impersonatedBy && <div className="text-grey40 dark:text-nightMuted">via {entry.impersonatedBy}</div>}
                                </td>
                                <td className={td}><span className={pill}>{entry.method}</span> <span className="break-all">{entry.path.replace('/eventopia/', '')}</span></td>
                                <td className={td}>
                                    {(() => {
                                        const target = drawerFor(entry);
                                        return target ? <button type="button" onClick={() => setDrawer(target)} className="font-semibold underline">{entry.resource}</button> : entry.resource;
                                    })()}
                                    {entry.resourceId && <div className="break-all text-grey40 dark:text-nightMuted">{entry.resourceId}</div>}
                                </td>
                                <td className={td}>
                                    <button type="button" className={smallBtn} onClick={() => setSelected(entry)}>View changes</button>
                                </td>
                            </tr>
                        ))}
                        {(data?.data ?? []).length === 0 && <EmptyRow colSpan={5} loading={isLoading} text="No audit entries yet." />}
                    </tbody>
                </table>
            </div>
            {drawer?.kind === 'event' && <EventEditor eventId={drawer.id} onClose={() => setDrawer(null)} />}
            {drawer?.kind === 'organization' && <OrganizationEditor organizerId={drawer.id} onClose={() => setDrawer(null)} />}
            {selected && <ChangesModal entry={selected} onClose={() => setSelected(null)} />}
            <Pager page={page} limit={LIMIT} total={data?.total ?? 0} onPage={setPage} />
        </div>
    );
}
